import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, Truck, Leaf, MapPin } from "lucide-react";
import { getProduct, getReviews, getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

const ProductDetail = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [related, setRelated] = useState([]);
    const [imageIndex, setImageIndex] = useState(0);
    const [qty, setQty] = useState(1);
    const [sticky, setSticky] = useState(false);
    const { add, setOpen } = useCart();

    useEffect(() => {
        setImageIndex(0);
        setQty(1);
        let active = true;
        // Standard data-fetching on slug change. Cancellation handled via `active` flag.
        (async () => {
            try {
                const p = await getProduct(slug);
                if (!active) return;
                setProduct(p);
                const items = await getProducts({ category: p.category });
                if (!active) return;
                setRelated(items.filter((x) => x.slug !== p.slug).slice(0, 3));
            } catch (_e) {
                if (active) setProduct(null);
            }
            try {
                const rs = await getReviews(slug);
                if (active) setReviews(rs);
            } catch (_e) {
                /* non-fatal */
            }
        })();
        window.scrollTo({ top: 0 });
        return () => {
            active = false;
        };
    }, [slug]);

    useEffect(() => {
        const onScroll = () => setSticky(window.scrollY > 600);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!product) {
        return (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 text-[#4A4A46]">
                Loading bar&hellip;
            </div>
        );
    }

    const onAdd = () => {
        add(product, qty);
        toast.success(`${product.name} added to cart.`);
    };

    return (
        <div data-testid="product-detail-page" className="bg-[#FAF9F6]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6">
                <nav className="text-[12px] uppercase tracking-[0.22em] text-[#737562]">
                    <Link to="/shop" className="hover:text-[#1C1C1A]">
                        Shop
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-[#1C1C1A]">{product.name}</span>
                </nav>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pb-24">
                {/* Image gallery */}
                <div className="lg:col-span-7">
                    <div className="product-image-wrap aspect-[4/5]">
                        <img
                            src={product.images[imageIndex]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            data-testid="product-main-image"
                        />
                    </div>
                    {product.images.length > 1 && (
                        <div className="mt-4 grid grid-cols-4 gap-3">
                            {product.images.map((src, i) => (
                                <button
                                    key={src + i}
                                    onClick={() => setImageIndex(i)}
                                    className={`product-image-wrap aspect-square ${
                                        i === imageIndex ? "ring-1 ring-[#1C1C1A]" : ""
                                    }`}
                                    data-testid={`thumb-${i}`}
                                >
                                    <img
                                        src={src}
                                        alt={`${product.name} ${i + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="lg:col-span-5 lg:pt-4">
                    <p className="label">{product.category.replace("-", " ")}</p>
                    <h1
                        className="font-serif text-4xl sm:text-5xl mt-5 leading-tight"
                        data-testid="product-name"
                    >
                        {product.name}
                    </h1>
                    <p className="mt-3 text-[#4A4A46]">{product.short_description}</p>
                    <p
                        className="mt-6 font-serif text-2xl"
                        data-testid="product-detail-price"
                    >
                        ${product.price.toFixed(2)} AUD
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#737562]">
                        {product.weight_g}g &middot; Handmade in Darwin
                    </p>

                    <hr className="divider my-8" />

                    <div className="text-[#4A4A46] leading-relaxed space-y-4">
                        {product.description.split("\n\n").map((para, i) => (
                            <p key={i}>{para}</p>
                        ))}
                    </div>

                    <div className="mt-8 flex items-center gap-4">
                        <div className="inline-flex items-center border border-[#C7C0B3]">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="w-10 h-12 flex items-center justify-center"
                                data-testid="detail-qty-decrease"
                                aria-label="Decrease quantity"
                            >
                                <Minus size={14} />
                            </button>
                            <span
                                className="w-12 text-center"
                                data-testid="detail-qty"
                            >
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                className="w-10 h-12 flex items-center justify-center"
                                data-testid="detail-qty-increase"
                                aria-label="Increase quantity"
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                        <button
                            onClick={onAdd}
                            className="btn-primary flex-1"
                            data-testid="add-to-cart-button"
                        >
                            Add to Cart
                        </button>
                    </div>

                    <button
                        onClick={() => {
                            add(product, qty);
                            setOpen(true);
                        }}
                        className="mt-3 w-full text-[12px] uppercase tracking-[0.22em] text-[#4A4A46] hover:text-[#1C1C1A] py-3 border-b border-[#E3DED5]"
                        data-testid="buy-now-button"
                    >
                        Buy Now &middot; Apple Pay &middot; Google Pay &middot; Shop Pay
                    </button>

                    <div className="mt-8 space-y-4 text-sm text-[#4A4A46]">
                        <div className="flex items-start gap-3">
                            <Truck size={16} strokeWidth={1.4} className="mt-0.5 text-[#6A7059]" />
                            <span>Free shipping over $80 within Australia.</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Leaf size={16} strokeWidth={1.4} className="mt-0.5 text-[#6A7059]" />
                            <span>Buy 3, save 15% &middot; Buy 5, save 20% &middot; Gift box available at checkout.</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={16} strokeWidth={1.4} className="mt-0.5 text-[#6A7059]" />
                            <span>Handmade in Darwin, Australia.</span>
                        </div>
                    </div>

                    <p className="mt-8 text-[13px] text-[#737562] leading-relaxed italic">
                        Because our soaps are handcrafted and naturally coloured, every bar
                        may vary slightly. That&rsquo;s part of what makes them special.
                    </p>
                </div>
            </div>

            {/* Detail sections */}
            <div className="bg-[#F0EBE1] py-20 lg:py-28 px-6 lg:px-10">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
                    <div data-testid="ingredients-block">
                        <p className="label">Ingredients</p>
                        <ul className="mt-6 space-y-2 text-[#1C1C1A]">
                            {product.ingredients.map((ing) => (
                                <li key={ing} className="text-[15px]">
                                    &mdash; {ing}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div data-testid="scent-feel-block">
                        <p className="label">Scent</p>
                        <p className="mt-4 text-[#1C1C1A] font-serif text-xl leading-snug">
                            {product.scent_profile}
                        </p>
                        <p className="label mt-10">Feel &amp; Lather</p>
                        <p className="mt-4 text-[#1C1C1A] font-serif text-xl leading-snug">
                            {product.lather}
                        </p>
                        <p className="label mt-10">Usage &amp; Storage</p>
                        <p className="mt-4 text-[#4A4A46] leading-relaxed">
                            {product.usage} Keep on a draining dish between uses to extend the
                            life of the bar.
                        </p>
                    </div>
                </div>

                {/* Botanical Infusion */}
                <div
                    className="max-w-5xl mx-auto mt-20 border-t border-[#C7C0B3] pt-16"
                    data-testid="botanical-infusion-block"
                >
                    <p className="label">Botanical Infusion</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-[#1C1C1A] leading-tight">
                        How this bar is naturally coloured.
                    </h2>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 text-[15px] text-[#4A4A46]">
                        <div>
                            <p className="label text-[10px]">Infused Oil</p>
                            <p className="mt-2 text-[#1C1C1A]">{product.botanical_infusion.oil}</p>
                        </div>
                        <div>
                            <p className="label text-[10px]">Botanicals</p>
                            <p className="mt-2 text-[#1C1C1A]">
                                {product.botanical_infusion.botanicals.length > 0
                                    ? product.botanical_infusion.botanicals.join(", ")
                                    : "None &mdash; undyed."}
                            </p>
                        </div>
                        <div>
                            <p className="label text-[10px]">Natural Colour</p>
                            <p className="mt-2 text-[#1C1C1A]">
                                {product.botanical_infusion.colour_note}
                            </p>
                        </div>
                    </div>
                    <p className="mt-8 text-[#4A4A46] leading-relaxed max-w-3xl">
                        Many of our oils are slowly infused with herbs, flowers and roots for
                        weeks before they ever become soap. The colour shifts subtly from batch
                        to batch &mdash; the way honest, naturally coloured soap should.
                    </p>
                </div>
            </div>

            {/* Reviews */}
            <section className="py-20 px-6 lg:px-10 bg-[#FAF9F6] border-t border-[#E3DED5]" data-testid="product-reviews-section">
                <div className="max-w-5xl mx-auto">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                        <div>
                            <p className="label">Customer Reviews</p>
                            <h2 className="font-serif text-3xl sm:text-4xl mt-4">
                                {reviews.length > 0 ? "What customers say." : "No reviews yet."}
                            </h2>
                        </div>
                        <Link
                            to={`/reviews?bar=${product.slug}`}
                            className="btn-secondary"
                            data-testid="product-leave-review-button"
                        >
                            Leave a Review
                        </Link>
                    </div>
                    {reviews.length === 0 ? (
                        <p className="text-[#4A4A46] max-w-xl" data-testid="product-reviews-empty">
                            Be the first to share your experience of this bar.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {reviews.map((r) => (
                                <div key={r.id} className="border border-[#E3DED5] bg-white p-8" data-testid={`product-review-${r.id}`}>
                                    <div className="flex gap-1 text-[#6A7059]">
                                        {Array.from({ length: r.rating }).map((_, i) => (
                                            <span key={`${r.id}-star-${i}`} aria-hidden="true">
                                                &#9733;
                                            </span>
                                        ))}
                                    </div>
                                    <p className="font-serif text-xl mt-4">{r.title}</p>
                                    <p className="mt-2 text-[#4A4A46]">{r.body}</p>
                                    <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#737562]">
                                        {r.display_name || r.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
                <section className="py-20 px-6 lg:px-10 bg-[#FAF9F6] border-t border-[#E3DED5]">
                    <div className="max-w-7xl mx-auto">
                        <p className="label">You may also like</p>
                        <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-10">
                            More from this collection.
                        </h2>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                            {related.map((p) => (
                                <ProductCard key={p.slug} product={p} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Sticky mobile add-to-cart */}
            <div
                data-testid="sticky-add-to-cart"
                className={`fixed bottom-0 left-0 right-0 z-30 bg-[#FAF9F6] border-t border-[#E3DED5] px-4 py-3 flex items-center gap-3 lg:hidden transition-transform duration-300 ${
                    sticky ? "translate-y-0" : "translate-y-full"
                }`}
            >
                <div className="flex-1">
                    <p className="font-serif text-base leading-tight">{product.name}</p>
                    <p className="text-sm text-[#4A4A46]">${product.price.toFixed(2)}</p>
                </div>
                <button onClick={onAdd} className="btn-primary py-3 px-5">
                    Add
                </button>
            </div>
        </div>
    );
};

export default ProductDetail;
